import { BadgeAdapter } from 'stores/adapters/bex-api/search';
import { createSearchAndFilterDisplayOptions } from 'stores/adapters/bex-api/search/search-sort-filter-adapter';
import { mapMessageModel } from 'src/common/stores/adapters/bex-api/search/search-results-adapter';

import { HotelMapModel, MapMarker, SheetModel } from 'models';
import { MarkerIcon } from 'models/hotel/map-types/map-marker';

import { FullPropertySearch, MapIconType, PropertyMapMigration } from '__generated__/api';

export const mapMap = (query: FullPropertySearch.Query | PropertyMapMigration.Query): HotelMapModel | null => {
  const { map } = query.propertySearch;

  if (!map) {
    return null;
  }

  return {
    ...map,
    filterDisplayOptions: createMapFilterDisplayOptions(),
    messagingModel: map.messagingModel && mapMessageModel(map.messagingModel),
    markers: getMapMarkers(query),
    showSearchButton: false,
    searchMessage: null,
    ...adaptMapBounds(query)
  };
};

const adaptMapBounds = (query: FullPropertySearch.Query | PropertyMapMigration.Query): any => {
  const { map } = query.propertySearch;
  const ne = map.bounds && map.bounds.northeast;
  const sw = map.bounds && map.bounds.southwest;

  return {
    center: {
      lat: map.center && map.center.latitude,
      lng: map.center && map.center.longitude
    },
    bounds: {
      ne: {
        lat: ne && ne.latitude,
        lng: ne && ne.longitude
      },
      sw: {
        lat: sw && sw.latitude,
        lng: sw && sw.longitude
      }
    }
  };
};

export const createMapFilterDisplayOptions = (): any => {
  const mapFiltersBlackList = ['sort'];
  const mapFilters = Object.assign({}, createSearchAndFilterDisplayOptions());

  mapFilters.filters.displayFields = mapFilters.filters.displayFields.filter(item => mapFiltersBlackList.indexOf(item) === -1);

  return mapFilters;
};

const getMapMarkers = (query: PropertyMapMigration.Query): MapMarker[] => {
  const { map } = query.propertySearch;
  const mapMarkers = map && map.markers;

  if (!mapMarkers) {
    return null;
  }

  return mapMarkers.map(marker => {
    let markerSheet = null;
    if (marker.__typename === 'Property') {
      markerSheet = getSheet(marker as PropertyMapMigration.Properties);
    }
    if (marker.__typename === 'PointOfInterest') {
      markerSheet = {
        title: marker.name,
        id: -1
      };
    }

    return {
      position: {
        lat: marker.mapMarker.latLong.latitude,
        lng: marker.mapMarker.latLong.longitude
      },
      label: marker.mapMarker.label,
      icon: MarkerIcon[marker.mapMarker.icon],
      sheet: markerSheet
    };
  });
};

const getSheet = (property: FullPropertySearch.Properties): SheetModel | null => {
  const primaryBadge = property.offerBadge && property.offerBadge.primary;
  const secondaryBadge = property.offerBadge && property.offerBadge.secondary;
  const badge = primaryBadge ? primaryBadge : secondaryBadge;

  const price = property.price;
  const lead = price && price.lead && price.lead.formatted;

  return {
    badgeText: BadgeAdapter.getBadge(badge).text,
    badgeTheme: BadgeAdapter.getBadge(badge).theme,
    distanceFromPOI: property.destinationInfo && property.destinationInfo.distanceFromMessaging,
    id: Number(property.id),
    leadPrice: property.mapMarker.icon === MapIconType.SOLD_OUT ? null : lead,
    linkOff: property.infositeURL,
    photoUrl: property.image && property.image.url,
    priceMessages: property.price && property.price.priceMessages && property.price.priceMessages.map(message => message.value),
    title: property.name,
    strikeoutPrice: price && price.strikeOut && price.strikeOut.formatted,
    strikeoutDisclaimer: price && price.disclaimer && price.disclaimer.value,
    subtitle: property.reviews && property.reviews.localizedSubtitle,
    vipMessage: property.vipMessaging,
    options: price && price.options
  };
};
